import {
  PartnerComparisonStyledTable,
  PartnerComparisonTableInlineExpander,
  PartnerComparisonTableBodyRow,
  PartnerComparisonTableBodyTD,
  PartnerComparisonTableHeadRow,
  PartnerComparisonTableHeadTD,
} from './partner_comparison_table.styles'
import { Text } from '@amzn/storm-ui'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PartnerDetails } from '../../../directory/components/partner_comparison/compared_partners_context'
import { CertificationTag } from '../../../../generated/graphql'
import { ClaimedByPartnerIcon } from '../../../directory/components/legends/legends.styles'
import { directoryStrings } from '../../../directory/directory_i18n_mappings'
import { clickPartnerComparisonDetailsMetricHandler } from '../../../../util/partner_comparison_metrics_helper'

interface PartnerComparisonTableProps {
  tableLabelKey: string
  translationLevelTokenMap: Map<string, string>
  translationTokenMap: Map<string, Map<string, string>>
  selectedPartners: PartnerDetails[]
  emptyColumnCount: number
  certificationCount?: number
}

export const CertificationsComparisonTable: React.FunctionComponent<PartnerComparisonTableProps> = (props: PartnerComparisonTableProps): JSX.Element => {
  const { tableLabelKey, translationLevelTokenMap, translationTokenMap, selectedPartners, emptyColumnCount, certificationCount } = props
  const [isExpanderOpen, setIsExpanderOpen] = useState(true)
  const { t } = useTranslation()

  const handleClickExpander = (): void => {
    clickPartnerComparisonDetailsMetricHandler('partnerComparison.page.certifications.expander.click')
    setIsExpanderOpen(prev => !prev)
  }

  const certificationsTable = useMemo(() => {
    return (
      <PartnerComparisonStyledTable>
        {Array.from(translationLevelTokenMap.keys()).map((levelTag, index) => {
          const levelValue = translationLevelTokenMap.get(levelTag) ?? ''
          const courseTagsMap = translationTokenMap.get(levelTag)
          return (
            <>
              <thead>
                <PartnerComparisonTableHeadRow key={`${levelTag}-${index}`}>
                  <PartnerComparisonTableHeadTD key={index}>
                    <Text key={index} type={'h5'}>
                      {t(levelValue)}
                    </Text>
                  </PartnerComparisonTableHeadTD>
                </PartnerComparisonTableHeadRow>
              </thead>
              <tbody>
                {courseTagsMap &&
                  Array.from(courseTagsMap.keys()).map((courseTag, iterator) => {
                    return (
                      <PartnerComparisonTableBodyRow key={iterator} data-testid={`${directoryStrings.TABLE_CERTIFICATIONS}-${levelValue}-${courseTag}`}>
                        <PartnerComparisonTableBodyTD key={iterator}>
                          <Text key={iterator}>{t(courseTagsMap.get(courseTag) ?? '')}</Text>
                        </PartnerComparisonTableBodyTD>
                        {selectedPartners.map((partner, iterator2) => {
                          const certTagsBasedOnLevel = partner.certifications?.filter(certEntry => certEntry.level === levelTag).map(certEntry => certEntry.tag)
                          return (
                            <PartnerComparisonTableBodyTD key={iterator2}>
                              {certTagsBasedOnLevel?.includes(courseTag as CertificationTag) ? (
                                <ClaimedByPartnerIcon key={`${courseTag}-${iterator2}`} role={'img'} aria-label={t(directoryStrings.CLAIMED_BY_PARTNER_ICON)} />
                              ) : (
                                <></>
                              )}
                            </PartnerComparisonTableBodyTD>
                          )
                        })}
                        {emptyColumnCount > 0 && [...Array(emptyColumnCount)].map((_, i) => <PartnerComparisonTableBodyTD key={i} />)}
                      </PartnerComparisonTableBodyRow>
                    )
                  })}
              </tbody>
            </>
          )
        })}
      </PartnerComparisonStyledTable>
    )
  }, [selectedPartners, emptyColumnCount, t, translationLevelTokenMap, translationTokenMap])

  return (
    <PartnerComparisonTableInlineExpander
      label={
        <Text type={'h3'} textColor={'base'}>
          {t(tableLabelKey, { count: certificationCount })}
        </Text>
      }
      isOpen={isExpanderOpen}
      handleClick={handleClickExpander}
    >
      {certificationsTable}
    </PartnerComparisonTableInlineExpander>
  )
}
